import React from 'react';
import { Tooltip } from 'antd';
import {
  ArrowUpOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  RiseOutlined,
} from '@ant-design/icons';
import './index.less';

/** 顶部对比指标 */
export interface DailySummary {
  /** 当日 jobId（用于头部展示，可省略以仅显示对比 Job） */
  todayJobName?: string;
  /** 对比的 jobId 名 */
  compareJobName: string;
  /** 当前通过率（0-100） */
  currentPassRate: number;
  /** 对比通过率（0-100） */
  comparePassRate: number;
  /** 异常率（千分比） */
  anomalyRate: number;
  /** 异常率阈值（千分比） */
  anomalyThreshold: number;
  /** 新增成功场景个数 */
  newSuccessSceneCount: number;
  /** 新增失败场景个数 */
  newFailSceneCount: number;
}

interface DailySummaryCardProps {
  summary: DailySummary;
  /** 自定义标题，默认「今日 Daily 仿真结果」；传 ``null`` 或空串可隐藏 */
  title?: string | null;
  /**
   * 自定义「对比 Job」节点。传入后会替换默认的 jobName 文本，常用于把对比 Job
   * 渲染成下拉选择器（``<Select />``）。
   */
  compareSlot?: React.ReactNode;
  /** 点击「新增成功场景」卡片回调；传入后该卡片可点击，并附 hover 高亮。 */
  onClickNewSuccess?: () => void;
  /** 点击「新增失败场景」卡片回调。 */
  onClickNewFail?: () => void;
  /** 点击触发模式：默认整卡可点；可选仅数字可点。 */
  clickablePart?: 'card' | 'value';
}

/**
 * Daily 仿真顶部统计卡：通过率 / 异常率 / 新增成功 / 新增失败 四指标。
 * 同时被 ``DailySimulation`` 顶部和 ``JobList`` 行展开内容复用。
 */
const DailySummaryCard: React.FC<DailySummaryCardProps> = ({
  summary,
  title = '今日 Daily 仿真结果',
  compareSlot,
  onClickNewSuccess,
  onClickNewFail,
  clickablePart = 'card',
}) => {
  const diff = +(summary.currentPassRate - summary.comparePassRate).toFixed(2);
  const diffPositive = diff >= 0;
  const anomalyOk = summary.anomalyRate <= summary.anomalyThreshold;

  const isCardClickable = clickablePart === 'card';

  return (
    <div className="daily-summary-card">
      <div className="daily-summary-header">
        {title && <span className="daily-summary-title">{title}</span>}
        <span className="daily-summary-compare">
          {summary.todayJobName && (
            <>
              今日 Job: <span className="daily-summary-compare-id">{summary.todayJobName}</span>
              <span className="daily-summary-compare-sep">·</span>
            </>
          )}
          对比 Job:{' '}
          {compareSlot ?? (
            <span className="daily-summary-compare-id">{summary.compareJobName}</span>
          )}
        </span>
      </div>

      <div className="daily-summary-metrics">
        <div className="daily-summary-metric">
          <div className="daily-summary-metric-icon daily-summary-metric-icon--success">
            <RiseOutlined />
          </div>
          <div className="daily-summary-metric-body">
            <div className="daily-summary-metric-label">当前通过率</div>
            <div className="daily-summary-metric-value">
              {summary.currentPassRate.toFixed(2)}%
              <span className={`daily-summary-metric-diff ${diffPositive ? 'is-up' : 'is-down'}`}>
                <ArrowUpOutlined rotate={diffPositive ? 0 : 180} />
                {diffPositive ? '+' : ''}
                {diff}%
              </span>
            </div>
            <div className="daily-summary-metric-sub">
              对比通过率 {summary.comparePassRate.toFixed(2)}%
            </div>
          </div>
        </div>

        <div className="daily-summary-metric">
          <div
            className={`daily-summary-metric-icon ${
              anomalyOk ? 'daily-summary-metric-icon--warn' : 'daily-summary-metric-icon--danger'
            }`}
          >
            <WarningFilled />
          </div>
          <div className="daily-summary-metric-body">
            <div className="daily-summary-metric-label">
              异常率 (阈值 {summary.anomalyThreshold}‰)
            </div>
            <div className="daily-summary-metric-value">
              {summary.anomalyRate.toFixed(2)}‰
              <span className={`daily-summary-metric-tag ${anomalyOk ? 'is-ok' : 'is-bad'}`}>
                {anomalyOk ? '正常' : '异常'}
              </span>
            </div>
          </div>
        </div>

        <Tooltip
          title={onClickNewSuccess ? '点击查看新增成功场景对应的任务列表' : ''}
        >
          <div
            className={
              `daily-summary-metric${onClickNewSuccess && isCardClickable ? ' is-clickable' : ''}`
            }
            onClick={isCardClickable ? onClickNewSuccess : undefined}
            role={onClickNewSuccess && isCardClickable ? 'button' : undefined}
          >
            <div className="daily-summary-metric-icon daily-summary-metric-icon--success">
              <CheckCircleFilled />
            </div>
            <div className="daily-summary-metric-body">
              <div className="daily-summary-metric-label">新增成功场景</div>
              <div
                className={`daily-summary-metric-value${onClickNewSuccess && !isCardClickable ? ' is-clickable' : ''}`}
                onClick={onClickNewSuccess && !isCardClickable ? onClickNewSuccess : undefined}
                role={onClickNewSuccess && !isCardClickable ? 'button' : undefined}
                style={onClickNewSuccess && !isCardClickable ? { cursor: 'pointer' } : undefined}
              >
                {summary.newSuccessSceneCount}
              </div>
            </div>
          </div>
        </Tooltip>

        <Tooltip title={onClickNewFail ? '点击查看新增失败场景对应的任务列表' : ''}>
          <div
            className={
              `daily-summary-metric${onClickNewFail && isCardClickable ? ' is-clickable' : ''}`
            }
            onClick={isCardClickable ? onClickNewFail : undefined}
            role={onClickNewFail && isCardClickable ? 'button' : undefined}
          >
            <div className="daily-summary-metric-icon daily-summary-metric-icon--danger">
              <CloseCircleFilled />
            </div>
            <div className="daily-summary-metric-body">
              <div className="daily-summary-metric-label">新增失败场景</div>
              <div
                className={`daily-summary-metric-value${onClickNewFail && !isCardClickable ? ' is-clickable' : ''}`}
                onClick={onClickNewFail && !isCardClickable ? onClickNewFail : undefined}
                role={onClickNewFail && !isCardClickable ? 'button' : undefined}
                style={onClickNewFail && !isCardClickable ? { cursor: 'pointer' } : undefined}
              >
                {summary.newFailSceneCount}
              </div>
            </div>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default DailySummaryCard;

/** Mock 数据，便于在尚未接入真实接口前直接使用。 */
export const MOCK_DAILY_SUMMARY: DailySummary = {
  todayJobName: 'JOB-0001',
  compareJobName: 'JOB-0042',
  currentPassRate: 96.85,
  comparePassRate: 95.42,
  anomalyRate: 0.8,
  anomalyThreshold: 1,
  newSuccessSceneCount: 12,
  newFailSceneCount: 3,
};
